<template>
  <div class="sidebar-page">
    <o-icon
      class="pb-2 pt-2 sidebar-toggle"
      @click="
        appStore.rightSidebar
          ? (appStore.rightSidebar = false)
          : (appStore.rightSidebar = true)
      "
      :icon="reduce ? 'arrow-collapse-right' : 'arrow-collapse-left'"
    ></o-icon>

    <o-sidebar
      position="static"
      :expand-on-hover="expandOnHover"
      :open="appStore.rightSidebar"
    >
      <section
        v-if="employeeStore.someEmployeesHasMissingFields(appStore.thisPayCycle)"
        class="has-background-danger has-text-centered"
        style="padding: 0em"
      >
        <span class="has-text-white is-emphasize"
          ><h3 class="has-text-white is-emphasize">
            Warning: Some Employees have missing critical fields! Please fix this one.
          </h3></span
        >
      </section>

      <section
        v-if="payslipStore.somePayslipsHasMissingFields(appStore.thisPayCycle)"
        class="has-background-danger has-text-centered"
        style="padding: 0em"
      >
        <span class="has-text-white is-emphasize"
          ><h3 class="has-text-white is-emphasize">
            Warning: Some Payslips have missing critical fields! Please fix this one.
          </h3></span
        >
      </section>

      <section
        class="has-background-warning has-text-centered is-warning"
        style="padding: 0em"
      >
        <o-button
          expanded
          size="large"
          icon-left="calendar"
          v-if="appStore.generateAccount.includes(route.name as string) && accountStore.accounts.length<=0"
          @click="accountStore.generateAccounts()"
          >Generate Accounts</o-button
        >
      </section>

      <section
        class="has-background-warning has-text-centered is-warning"
        style="padding: 0em"
      >
        <o-button
          expanded
          size="large"
          icon-left="calendar"
          v-if="appStore.generatePeriod.includes(route.name as string) && periodStore.periods.length<=0"
          @click="periodStore.generatePeriods()"
          >Generate Periods</o-button
        >
      </section>
      <section class="sidebar-section">
        <GlobalFilter />
      </section>
      <section class="sidebar-section" style="padding: 1em">
        <UploadFile @import-file="importFile" />
      </section>

      <section>
        <span> </span>
      </section>
    </o-sidebar>
  </div>
</template>

<script setup lang="ts">
import { defineComponent, ref, computed } from "vue";
import router from "@/router";

import { useRouter, useRoute } from "vue-router";
import { useProgrammatic } from "@oruga-ui/oruga-next";
import UploadFile from "./common/UploadFile.vue";
import { useAppStore } from "@/stores/app";
import { useDailyTimeStore } from "@/stores/dailytime";
import { useTransactStore } from "@/stores/transact";
import { usePayslipStore } from "@/stores/payslip";
import { usePeriodStore } from "@/stores/period";
import { useEmployeeStore } from "@/stores/employee";
import { useAccountStore } from "@/stores/account";
import { useCompanyStore } from "@/stores/company";

import { useUtil } from "@/composables/util";

import GlobalFilter from "./common/GlobalFilter.vue";
import { useCostStore } from "@/stores/cost";

const route = useRoute();
const { oruga } = useProgrammatic();

const appStore = useAppStore();
const dailytime = useDailyTimeStore();
const transactStore = useTransactStore();
const payslipStore = usePayslipStore();
const periodStore = usePeriodStore();
const employeeStore = useEmployeeStore();
const accountStore = useAccountStore();
const costStore = useCostStore();
const companyStore = useCompanyStore();

const expandOnHover = ref(false);
const mobile = ref("reduced");
const reduce = ref(false);

const importFile = async (payload: any) => {
  appStore.isLoading = true;
  if (payload.type == "dailytime") {
    await dailytime
      .importDailyTime(payload.data)
      .then(() => {
        appStore.isLoading = false;
        const fn = payload.filename;
        periodStore.savePeriod({
          uuid: appStore.thisPeriod.uuid,
          timelogs: (appStore as any).timelogs + "," + fn,
        } as any);
      })
      .finally(() => {
        appStore.isLoading = false;
      });
  } else if (payload.type == "transact") {
    const summary = await transactStore
      .importTransactions(payload.data)
      .finally(() => {
        appStore.isLoading = false;
      });
    if (summary) {
      const skipped = summary.skipped ?? [];
      const detail = skipped
        .slice(0, 5)
        .map((s: any) => `row ${s.row}: ${s.reason}`)
        .join("; ");
      const more = skipped.length > 5 ? ` (+${skipped.length - 5} more)` : "";

      // The exported guide row is scaffolding, not a failed row — it gets its own
      // sentence and stays out of the skipped count. It only warrants a warning when
      // it is the reason nothing came in.
      const examples = summary.examples ?? 0;
      const note = examples
        ? " Example row ignored — replace it with your data or delete it."
        : "";
      const attention = skipped.length > 0 || (examples > 0 && summary.imported == 0);

      oruga.notification.open({
        message: skipped.length
          ? // the skip detail has no trailing stop of its own, so add one before the note
            `Imported ${summary.imported}, skipped ${skipped.length}: ${detail}${more}.${note}`
          : `Imported ${summary.imported} row(s).${note}`,
        variant: attention ? "warning" : "success",
        position: "top",
        duration: skipped.length || examples ? 8000 : 4000,
        closable: true,
      });
    }
  } else if (payload.type == "employee") {
    await employeeStore.importEmployees(payload.data).finally(() => {
      appStore.isLoading = false;
    });
  } else if (payload.type == "cost") {
    await costStore.importCostCenters(payload.data).finally(() => {
      appStore.isLoading = false;
    });
  } else if (payload.type == "company") {
    await companyStore.importCompanies(payload.data).finally(() => {
      appStore.isLoading = false;
    });
  }
};
</script>

<style scoped>
.sidebar-page {
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-height: 100%;
  padding-top: 150px;
}
.sidebar-layout {
  display: flex;
  flex-direction: row;
  min-height: 100%;
}

/* keep Oruga's inner wrapper dark so wave overlay in App.vue shows on top */
.sidebar-page :deep(.o-side__content),
.sidebar-page :deep([class*="o-side"]) {
  background-color: #1e1b4b !important;
}

.sidebar-toggle {
  color: #a5b4fc !important;
  cursor: pointer;
  padding: 0.4rem 0.5rem;
}
.sidebar-toggle:hover {
  color: #e0e7ff !important;
}

.sidebar-section {
  background-color: transparent;
}

/* right-sidebar labels */
:deep(.o-field__label),
:deep(label) {
  color: #312e81 !important;
  text-shadow: none;
}

/* keep selects readable on dark bg */
:deep(.o-select select) {
  background: rgba(255,255,255,0.08) !important;
  color: #e0e7ff !important;
  border-color: rgba(165,180,252,0.3) !important;
}
/* inputs (e.g. year) get a light bg with dark text */
:deep(.o-input),
:deep(input) {
  background: rgba(255,255,255,0.88) !important;
  color: #1e1b4b !important;
  border-color: rgba(165,180,252,0.3) !important;
}
:deep(.o-select select option) {
  background: #1e1b4b;
  color: #e0e7ff;
}
</style>
